<template>
  <v-data-table
    :headers="displayHeaders"
    :items="itemsCopy"
    v-sortable-table="{ onEnd: sortTheHeadersAndUpdateTheKey }"
    :key="anIncreasingNumber"
    :value="value"
    @input="onInput"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <!-- Pass on all named slots -->
    <slot v-for="slot in Object.keys($slots)" :name="slot" :slot="slot" />

    <!-- Pass on all scoped slots -->
    <template v-for="slot in Object.keys($scopedSlots)" :slot="slot" slot-scope="scope"
      ><slot :name="slot" v-bind="scope"
    /></template>
  </v-data-table>
</template>

<script>
import Sortable from "sortablejs";

function watchClass(targetNode, classToWatch) {
  let lastClassState = targetNode.classList.contains(classToWatch);
  const observer = new MutationObserver(mutationsList => {
    for (let i = 0; i < mutationsList.length; i++) {
      const mutation = mutationsList[i];
      if (mutation.type === "attributes" && mutation.attributeName === "class") {
        const currentClassState = mutation.target.classList.contains(classToWatch);
        if (lastClassState !== currentClassState) {
          lastClassState = currentClassState;
          if (!currentClassState) {
            mutation.target.classList.add("sortHandle");
          }
        }
      }
    }
  });
  observer.observe(targetNode, { attributes: true });
}

export default {
  name: "DraggableMergingHeaderTable",
  props: {
    headers: Array,
    items: Array,
    value: Object
  },
  data: () => ({
    anIncreasingNumber: 1,
    headersCopy: [],
    displayHeaders: [],
    fixedItems: {},
    itemsCopy: []
  }),
  directives: {
    "sortable-table": {
      inserted: (el, binding) => {
        el.querySelectorAll("th:not(.data-table-expand-header)").forEach(draggableEl => {
          // Need a class watcher because sorting v-data-table rows asc/desc removes the sortHandle class
          watchClass(draggableEl, "sortHandle");
          draggableEl.classList.add("sortHandle");
        });
        Sortable.create(el.querySelector("tr"), binding.value ? { ...binding.value, handle: ".sortHandle" } : {});
      },
      update: (el, binding) => {
        el.querySelectorAll("th:not(.data-table-expand-header)").forEach(draggableEl => {
          // Need a class watcher because sorting v-data-table rows asc/desc removes the sortHandle class
          watchClass(draggableEl, "sortHandle");
          draggableEl.classList.add("sortHandle");
        });
        Sortable.create(el.querySelector("tr"), binding.value ? { ...binding.value, handle: ".sortHandle" } : {});
      }
    }
  },
  methods: {
    updateHeaders(value) {
      // The idea is to add new columns just in the end and keep the set order until then.
      let currentHeadersNames = this.displayHeaders.map(h => h.name);
      let newHeaderNames = value.map(h => h.name);
      let newHeaders = [];
      let repeatedHeaders = [];
      for (let header of value) {
        if (!currentHeadersNames.includes(header.name)) {
          header.related = []; // Every header has a list or related headers with the same name
          header.repeated = false;
          newHeaders.push(header);
          currentHeadersNames.push(header.name);
        } else {
          repeatedHeaders.push(header);
        }
      }

      this.headersCopy = this.headersCopy.concat(newHeaders);
      this.displayHeaders = this.displayHeaders.concat(newHeaders);

      repeatedHeaders.forEach(item => {
        // For every repeated elemenet the related array is populated
        let firstIndex = this.headersCopy.findIndex(elem => elem.text == item.text);
        item.repeated = true;
        this.headersCopy[firstIndex].related.push(item.value);
        this.headersCopy.push(item);
      });

      this.displayHeaders = this.displayHeaders.filter(h => newHeaderNames.includes(h.name));
      this.headersCopy = this.headersCopy.filter(h => newHeaderNames.includes(h.name));
    },

    updateItems() {
      // v-data-table shows the items properties so as we are using just one column,
      // we need all the items to have the info in the same property, thus I'm copying it to the same property
      this.itemsCopy.forEach(curItem => {
        this.displayHeaders.forEach(header => {
          let related = header.related;
          if (related && related.length > 0) {
            if (!curItem[header.value]) {
              for (let relItem of related) {
                if (curItem[relItem]) {
                  curItem[header.value] = curItem[relItem];
                }
              }
            }
          }
        });
      });
    },

    onInput() {
      this.$emit("input", this.itemsCopy);
    },
    sortTheHeadersAndUpdateTheKey(evt) {
      let hasExpandableFirstColumn =
        this.displayHeaders && this.displayHeaders.length > 0 && this.displayHeaders[0].value == "data-table-expand";
      const headersTmp = this.displayHeaders.slice();
      let oldIndex = evt.oldIndex;
      let newIndex = evt.newIndex;
      if (hasExpandableFirstColumn && newIndex < 1) {
        newIndex = 1;
      }
      if (newIndex >= headersTmp.length) {
        let k = newIndex - headersTmp.length + 1;
        while (k--) {
          headersTmp.push(undefined);
        }
      }
      headersTmp.splice(newIndex, 0, headersTmp.splice(oldIndex, 1)[0]);
      this.displayHeaders = headersTmp.slice();
      this.anIncreasingNumber += 1;
    }
  },
  watch: {
    headers(value) {
      this.itemsCopy = [...this.items];
      this.updateHeaders(value);
      this.updateItems();
    },
    items(value) {
      this.itemsCopy = [...value];
      this.updateItems();
    }
  }
};
</script>
